// The measure tool's state and transitions, ported from the Android app's MeasureSession.kt (the
// reducer mirrors the mockup's handlers one for one) and the straight half of MeasureMeasurement.kt.
// Pure: geodesy in, new session out. A transition that changes nothing returns the same object, so
// callers can skip work with `next === session`.

import { GeoPoint, destination, inverse, normalizeBearing, toRadians } from './geodesy';
import { PROFILE_SAMPLE_COUNT, samplesAlongGeodesic } from './ticks';

/** Which part of the sheet is up (mockup `step`). */
export type MeasureStep = 'Start' | 'PickStart' | 'Target';

/** Where the measurement starts from: a snapshot of the walker's fix, or a point picked on the map. */
export type MeasureStartSource = 'MyLocation' | 'MapPoint';

/** How a drag of the measured point is constrained. */
export type MeasureLock = 'Free' | 'Distance' | 'Bearing';

/** Moves a point onto the route (on the start's line) when snapping is live. */
export type MeasureSnap = (point: GeoPoint) => GeoPoint;

/**
 * Everything the measure tool remembers between gestures — the mockup's `state`, minus the UI-only
 * bits (toast, copied, range-sheet text) the view keeps beside it.
 *
 * [lockedRange] (m) and [lockedBearing] (true degrees) are captured by [setLock] and [placeRange].
 * [band] is only ever true straight after a range placement.
 */
export interface MeasureSession {
  readonly step: MeasureStep;
  readonly start: GeoPoint | null;
  readonly startSource: MeasureStartSource | null;
  readonly target: GeoPoint | null;
  readonly lock: MeasureLock;
  readonly lockedRange: number;
  readonly lockedBearing: number;
  readonly band: boolean;
  readonly routeOn: boolean;
  readonly lockMenuOpen: boolean;
  readonly rangeSheetOpen: boolean;
  readonly dragging: boolean;
}

/**
 * A fresh session (Android `MeasureSession()`), with Snap to route on: measuring along the hike or
 * recording is what the tool is for there. It only takes effect where the screen has a route (see
 * [routeActive]), so the main map still measures straight.
 */
export function initialSession(): MeasureSession {
  return {
    step: 'Start',
    start: null,
    startSource: null,
    target: null,
    lock: 'Free',
    lockedRange: 0.0,
    lockedBearing: 0.0,
    band: false,
    routeOn: true,
    lockMenuOpen: false,
    rangeSheetOpen: false,
    dragging: false,
  };
}

/** Back to the start picker with nothing measured, keeping the walker's Snap to route choice. */
export function restartSession(session: MeasureSession): MeasureSession {
  return { ...initialSession(), routeOn: session.routeOn };
}

export const isPlaced = (session: MeasureSession): boolean => session.target !== null;

/** Start set, nothing placed yet: the step that takes the second tap. */
export const isTargetStep = (session: MeasureSession): boolean => session.step === 'Target' && session.target === null;

/** Snapping is live only on a screen that has a route, with the switch on. */
export const routeActive = (session: MeasureSession, routeAvailable: boolean): boolean => routeAvailable && session.routeOn;

/** Shortest distance a bearing-locked point may sit from the start (mockup: 4 px ≈ 5 m). */
export const MIN_BEARING_LOCK_METERS = 5.0;

/** Half-width of the yellow accuracy band drawn after a range placement, in whole metres. */
export const RANGE_TOLERANCE_DEFAULT = 5;
export const RANGE_TOLERANCE_MIN = 1;
export const RANGE_TOLERANCE_MAX = 20;

/** A stored tolerance held to 1–20 m (Android `MeasureRangeTolerance.read`). */
export function clampRangeTolerance(metres: number | null | undefined): number {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return RANGE_TOLERANCE_DEFAULT;
  return Math.min(Math.max(Math.trunc(metres), RANGE_TOLERANCE_MIN), RANGE_TOLERANCE_MAX);
}

export function chooseMyLocation(session: MeasureSession, fix: GeoPoint): MeasureSession {
  if (session.step !== 'Start') return session;
  return { ...session, step: 'Target', start: fix, startSource: 'MyLocation' };
}

export function pickOnMap(session: MeasureSession): MeasureSession {
  return session.step !== 'Start' ? session : { ...session, step: 'PickStart' };
}

/** "Cancel" while picking a start: back to the start picker with nothing kept but the snap switch (mockup `reset`). */
export function cancelPickStart(session: MeasureSession): MeasureSession {
  return session.step !== 'PickStart' ? session : restartSession(session);
}

/**
 * A map tap. Sets the start while picking one, places the point in the target step, and is ignored
 * everywhere else (returns the same object). With snapping live both land on the route, so a
 * measurement on a hike starts and ends on the line.
 */
export function mapTap(session: MeasureSession, point: GeoPoint, isRouteActive: boolean, snap: MeasureSnap): MeasureSession {
  if (session.step === 'PickStart') {
    return { ...session, step: 'Target', start: isRouteActive ? snap(point) : point, startSource: 'MapPoint' };
  }
  if (isTargetStep(session) && !session.rangeSheetOpen) {
    return { ...session, target: isRouteActive ? snap(point) : point, lock: 'Free' };
  }
  return session;
}

export function openRangeSheet(session: MeasureSession): MeasureSession {
  return isTargetStep(session) ? { ...session, rangeSheetOpen: true } : session;
}

export function closeRangeSheet(session: MeasureSession): MeasureSession {
  return { ...session, rangeSheetOpen: false };
}

/**
 * "Place point": project the range along the true bearing, then lock the distance to it and draw
 * the accuracy band. Always straight — entering a range turns snapping off.
 */
export function placeRange(session: MeasureSession, distanceMeters: number, trueBearing: number): MeasureSession {
  if (session.start === null) return session;
  const bearing = normalizeBearing(trueBearing);
  return {
    ...session,
    routeOn: false,
    rangeSheetOpen: false,
    target: destination(session.start, bearing, distanceMeters).point,
    lock: 'Distance',
    lockedRange: distanceMeters,
    lockedBearing: bearing,
    band: true,
  };
}

/** Lock menu item (mockup `setLock`): capture the current range and bearing, close the menu. */
export function setLock(session: MeasureSession, mode: MeasureLock): MeasureSession {
  if (session.start === null || session.target === null) return session;
  const line = inverse(session.start, session.target);
  return {
    ...session,
    lockMenuOpen: false,
    lock: mode,
    lockedRange: line.distance,
    lockedBearing: line.initialBearing,
    band: mode === 'Distance' ? session.band : false,
  };
}

export function toggleLockMenu(session: MeasureSession): MeasureSession {
  return { ...session, lockMenuOpen: !session.lockMenuOpen };
}

/**
 * The Snap pill (mockup `toggleRoute`). Off: the point stays where it is. On: the point moves onto
 * the route. Either way the lock goes back to free and the band is dropped.
 */
export function toggleSnap(session: MeasureSession, snap: MeasureSnap): MeasureSession {
  if (session.routeOn) {
    return { ...session, routeOn: false, lock: 'Free', band: false, lockMenuOpen: false };
  }
  return {
    ...session,
    routeOn: true,
    lock: 'Free',
    band: false,
    lockMenuOpen: false,
    target: session.target === null ? null : snap(session.target),
  };
}

export function dragStarted(session: MeasureSession): MeasureSession {
  return isPlaced(session) ? { ...session, dragging: true, lockMenuOpen: false } : session;
}

/**
 * Mockup `applyLock`: where a dragged point lands. On the route when snapping; on the locked circle
 * around the start; on the locked bearing line (never closer than 5 m); else free.
 */
export function applyLock(
  raw: GeoPoint,
  start: GeoPoint,
  session: MeasureSession,
  isRouteActive: boolean,
  snap: MeasureSnap,
): GeoPoint {
  if (isRouteActive) return snap(raw);
  if (session.lock === 'Distance') {
    const bearing = inverse(start, raw).initialBearing;
    return destination(start, bearing, session.lockedRange).point;
  }
  if (session.lock === 'Bearing') {
    const line = inverse(start, raw);
    const offset = toRadians(line.initialBearing - session.lockedBearing);
    const along = Math.max(MIN_BEARING_LOCK_METERS, line.distance * Math.cos(offset));
    return destination(start, session.lockedBearing, along).point;
  }
  return raw;
}

export function dragged(session: MeasureSession, raw: GeoPoint, isRouteActive: boolean, snap: MeasureSnap): MeasureSession {
  if (!session.dragging || session.start === null) return session;
  return { ...session, target: applyLock(raw, session.start, session, isRouteActive, snap) };
}

/**
 * The start pin dragged to [raw]: onto the route when snapping. It is a map point from then on,
 * and the lock goes back to free, since a locked range or bearing was taken from the old start.
 */
export function startDragged(session: MeasureSession, raw: GeoPoint, isRouteActive: boolean, snap: MeasureSnap): MeasureSession {
  if (session.start === null) return session;
  return {
    ...session,
    start: isRouteActive ? snap(raw) : raw,
    startSource: 'MapPoint',
    lock: 'Free',
    band: false,
    lockMenuOpen: false,
  };
}

export function dragEnded(session: MeasureSession): MeasureSession {
  return { ...session, dragging: false };
}

/**
 * The screen's route changed underneath an open tool. Snapped: re-snap onto the new route, or, if
 * there is no route any more, fall back to straight. The measurement itself is kept.
 */
export function routeChanged(session: MeasureSession, routeAvailable: boolean, snap: MeasureSnap): MeasureSession {
  if (!session.routeOn) return session;
  if (!routeAvailable) return { ...session, routeOn: false };
  return { ...session, target: session.target === null ? null : snap(session.target) };
}

/**
 * What the placed point measures. Straight: the geodesic from the start. Snapped (route mode, built
 * by the web's own trail snapper): both ends on the route, the distance along it, the bearing still
 * the direct one from snapped start to target, and the profile following the route.
 */
export interface MeasureMeasurement {
  /** The start as measured from: projected onto the route in route mode. */
  start: GeoPoint;
  target: GeoPoint;
  routeMode: boolean;
  distance: number;
  /** Direct initial bearing, start → target, true degrees. */
  trueBearing: number;
  /** The route between the two snapped ends, start first; null when straight. */
  routePath: GeoPoint[] | null;
  /** Where the profile is sampled, [PROFILE_SAMPLE_COUNT] points start → target. */
  samplePoints: GeoPoint[];
  /** The route's own altitudes at [samplePoints] when every vertex has one; null when straight. */
  routeElevations: number[] | null;
}

/** The straight measurement (MeasureMeasurer.measure without a route), or null until placed. */
export function measureStraight(session: MeasureSession): MeasureMeasurement | null {
  if (session.start === null || session.target === null) return null;
  const line = inverse(session.start, session.target);
  return {
    start: session.start,
    target: session.target,
    routeMode: false,
    distance: line.distance,
    trueBearing: line.initialBearing,
    routePath: null,
    samplePoints: samplesAlongGeodesic(session.start, session.target, PROFILE_SAMPLE_COUNT),
    routeElevations: null,
  };
}
